// src/components/Common/ConfirmModal.js
// Reusable confirmation modal to replace browser alerts and confirms

import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './ConfirmModal.css';

/**
 * ConfirmModal - A styled confirmation modal component
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Called when modal is closed (cancel)
 * @param {function} onConfirm - Called when user confirms
 * @param {string} title - Modal title
 * @param {string} message - Main message text
 * @param {string[]} details - Optional array of detail items (bullet points)
 * @param {string} confirmText - Text for confirm button (default: "Confirmer")
 * @param {string} cancelText - Text for cancel button (default: "Annuler")
 * @param {string} type - Modal type: "danger" | "warning" | "info" (default: "warning")
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmer l'action",
  message,
  details = [],
  confirmText = "Confirmer",
  cancelText = "Annuler",
  type = "warning"
}) => {
  const modalRef = useRef(null);
  const confirmButtonRef = useRef(null);

  // Handle keyboard events
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onConfirm();
    }
  }, [onClose, onConfirm]);

  // Focus trap and keyboard handling
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Focus confirm button when modal opens
      setTimeout(() => confirmButtonRef.current?.focus(), 0);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'danger':
        return '🗑️';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '⚠️';
    }
  };

  const modalContent = (
    <div className="confirm-modal-overlay" onClick={onClose}>
      <div
        className={`confirm-modal ${type}`}
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        <div className="confirm-modal-header">
          <span className="confirm-modal-icon">{getIcon()}</span>
          <h3 id="confirm-modal-title">{title}</h3>
        </div>

        <div className="confirm-modal-body">
          {message && <p className="confirm-modal-message">{message}</p>}

          {details.length > 0 && (
            <ul className="confirm-modal-details">
              {details.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="confirm-modal-footer">
          <button
            type="button"
            className="confirm-modal-btn cancel"
            onClick={onClose}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`confirm-modal-btn confirm ${type}`}
            onClick={onConfirm}
            ref={confirmButtonRef}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ConfirmModal;
